import type { FlowNode } from './model.js';

/**
 * Where everything on the canvas goes (docs/41 §4): left to right, a column per depth, each node
 * centred on the block of its children (a tidy tree), so a run of single steps is a straight line.
 *
 * The gap before a column is narrow when no labelled line enters it and wide when one does, and in
 * a wide gap the label and the "+" have fixed places that never meet: the bus elbow 20 px after the
 * source, the label from 28 px (at most 82 wide), the "+" 28 px before the target.
 */
export const NODE_W = 164;
const GAP_PLAIN = 72, GAP_LABEL = 156, ROW_GAP = 14, PAD_X = 16, PAD_Y = 24;
const BUS = 20, PLUS_BEFORE = 28, ELBOW = 8;

/** About how wide each badge draws, to know how many rows a card's badges take in its 142 px. */
const BADGE_W: Record<FlowNode['badges'][number], number> = { new: 44, changed: 62, 'needs-recording': 122, 'needs-people': 98 };

function badgeRows(badges: FlowNode['badges']): number {
  let rows = 0, used = 0;
  for (const b of badges) {
    const w = BADGE_W[b];
    if (rows === 0 || used + 5 + w > 142) { rows++; used = w; } else used += 5 + w;
  }
  return rows;
}

/** A node's height, as its CSS draws it (border-box): heights are set, so lines meet card centres. */
export function nodeHeight(n: FlowNode): number {
  if (n.term) return 52;
  const lines = n.detail.length + (n.now !== undefined && n.now !== null ? 1 : 0);
  const rows = badgeRows(n.badges);
  return 2 + 9 + 20 + 6 + 18 + 16 * lines + (n.shared ? 19 : 0) + (rows > 0 ? 7 + 16 * rows + 5 * (rows - 1) : 0) + 10;
}

export interface Placed { node: FlowNode; x: number; y: number; h: number; col: number }
export interface Edge {
  from: Placed; to: Placed; label: string | null; slot: string;
  path: string; head: string;
  labelX: number; labelY: number;
  plusX: number; plusY: number;
}
export interface Layout { placed: Placed[]; edges: Edge[]; width: number; height: number; byKey: Map<string, Placed> }

interface Box { node: FlowNode; h: number; block: number; col: number; y: number; kids: Box[] }

export function layout(root: FlowNode): Layout {
  const build = (n: FlowNode): Box => {
    const kids = n.children.map((c) => build(c.node));
    const h = nodeHeight(n);
    const total = kids.reduce((s, k) => s + k.block, 0) + ROW_GAP * Math.max(0, kids.length - 1);
    return { node: n, h, block: Math.max(h, total), col: 0, y: 0, kids };
  };
  const place = (b: Box, col: number, top: number) => {
    b.col = col;
    b.y = top + (b.block - b.h) / 2;
    const total = b.kids.reduce((s, k) => s + k.block, 0) + ROW_GAP * Math.max(0, b.kids.length - 1);
    let cur = top + (b.block - total) / 2;
    for (const k of b.kids) { place(k, col + 1, cur); cur += k.block + ROW_GAP; }
  };
  const tree = build(root);
  place(tree, 0, PAD_Y);

  const boxes: Box[] = [];
  const walk = (b: Box) => { boxes.push(b); b.kids.forEach(walk); };
  walk(tree);
  const ncol = Math.max(...boxes.map((b) => b.col)) + 1;
  const labelled = new Array<boolean>(ncol).fill(false);
  for (const b of boxes) b.node.children.forEach((c, i) => { if (c.label) labelled[b.kids[i]!.col] = true; });
  const colx = [PAD_X];
  for (let c = 1; c < ncol; c++) colx.push(colx[c - 1]! + NODE_W + (labelled[c] ? GAP_LABEL : GAP_PLAIN));

  const placed: Placed[] = boxes.map((b) => ({ node: b.node, x: colx[b.col]!, y: b.y, h: b.h, col: b.col }));
  const byKey = new Map(placed.map((p) => [p.node.key, p]));
  const edges: Edge[] = [];
  const corner = (bx: number, yFrom: number, yTo: number, r: number) => {
    const s = yTo > yFrom ? 1 : -1;
    return `Q${bx},${yFrom} ${bx},${yFrom + s * r} V${yTo - s * r} Q${bx},${yTo} ${bx + r},${yTo}`;
  };
  for (const b of boxes) {
    if (b.kids.length === 0) continue;
    const from = byKey.get(b.node.key)!;
    const x1 = from.x + NODE_W;
    const y1 = from.y + from.h / 2;
    const bx = x1 + BUS;
    const ys = b.kids.map((k) => k.y + k.h / 2);
    const single = b.kids.length === 1;
    b.kids.forEach((k, i) => {
      const to = byKey.get(k.node.key)!;
      const x2 = to.x;
      const y2 = to.y + to.h / 2;
      const dy = y2 - y1;
      let path: string;
      let lx: number;
      if (single && Math.abs(dy) < 0.5) {
        path = `M${x1},${y1} H${x2 - 7}`;
        lx = x1 + 10;
      } else {
        const r = Math.min(ELBOW, Math.abs(dy) / 2);
        if (Math.abs(dy) < 0.5) path = `M${bx},${y1} H${x2 - 7}`;
        else if (y2 === Math.min(...ys) || y2 === Math.max(...ys)) path = `M${x1},${y1} H${bx - r} ${corner(bx, y1, y2, r)} H${x2 - 7}`;
        else {
          const s = dy > 0 ? 1 : -1;
          path = `M${bx},${y1} V${y2 - s * r} Q${bx},${y2} ${bx + r},${y2} H${x2 - 7}`;
        }
        // The outermost branches carry the trunk from the source; the ones between join the bus.
        lx = bx + 8;
      }
      const c = b.node.children[i]!;
      edges.push({
        from, to, label: c.label, slot: c.slot, path,
        head: `M${x2 - 7},${y2 - 3.5} L${x2},${y2} L${x2 - 7},${y2 + 3.5} Z`,
        labelX: lx, labelY: y2, plusX: x2 - PLUS_BEFORE, plusY: y2,
      });
    });
  }
  const width = colx[ncol - 1]! + NODE_W + PAD_X;
  const height = tree.block + 2 * PAD_Y;
  return { placed, edges, width, height, byKey };
}
